import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import { StatusTag } from "../../components/common/registerUtils";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  API_ASSET_STOCK_COUNTS,
  API_ASSET_STOCK_COUNTS_EXPORT,
} from "../../utils/apis";
import {
  CAN_APPROVE_ASSET_STOCK_COUNT,
  CAN_PERFORM_ASSET_STOCK_COUNT,
} from "../../utils/constants";
import {
  createViewAction,
  formatDate,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_ASSET_STOCK_COUNTS } from "../../utils/urls";
import {
  APPROVAL_STATUSES,
  AssetSummary,
  approvalSection,
  assetExport,
  auditFields,
  createAssetApprovalActionForm,
  createdAtFilter,
  showNumber,
  toFilterOptions,
  useAssetPermissions,
  useAssetPicker,
} from "./assetUtils";

// negative = shortage, positive = surplus
const Variance = ({ value }: { value: number }) => {
  if (value === null || value === undefined) return null;
  const color = value < 0 ? "red" : value > 0 ? "orange" : "green";
  const label = value < 0 ? "shortage" : value > 0 ? "surplus" : "no variance";
  return (
    <span style={{ color, whiteSpace: "nowrap" }}>
      {value > 0 ? `+${value}` : value} ({label})
    </span>
  );
};

export const useAssetStockCounts = ({ status }: { status?: string } = {}) => {
  const can = useAssetPermissions();

  const tableColumns = () => [
    { id: "count_id", header: "Count ID" },
    {
      id: "asset_name",
      header: "Asset",
      filter: { type: "text", key: "asset" },
    },
    { id: "asset_category", header: "Category" },
    { id: "location", header: "Location" },
    { id: "system_quantity", header: "System Qty" },
    { id: "physical_quantity", header: "Physical Qty" },
    { id: "variance", header: "Variance" },
    {
      id: "status",
      header: "Status",
      ...(status
        ? {}
        : {
            filter: {
              type: "select",
              options: toFilterOptions(APPROVAL_STATUSES),
            },
          }),
    },
    { id: "created_at", header: "Counted On", filter: createdAtFilter },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      system_quantity: showNumber(item?.system_quantity),
      physical_quantity: showNumber(item?.physical_quantity),
      variance: <Variance value={item?.variance} />,
      status: <StatusTag status={item?.status} />,
      created_at: formatDate(item?.created_at),
      actions: (
        <FormActions
          actions={[createViewAction(URL_ASSET_STOCK_COUNTS, item?.id)]}
        />
      ),
    }));

  const viewData = (item: any) => ({
    sections: [
      {
        title: "Stock Count Details",
        fields: {
          count_id: item?.count_id,
          asset: item?.asset_name,
          category: item?.asset_category,
          location: item?.location,
          system_quantity: showNumber(item?.system_quantity),
          physical_quantity: showNumber(item?.physical_quantity),
          variance: <Variance value={item?.variance} />,
          reason_for_variance: item?.reason_for_variance,
          status: <StatusTag status={item?.status} />,
        },
      },
      approvalSection(item),
      { title: "Record", fields: auditFields(item) },
    ],
  });

  return {
    tableTitle: "Asset Stock Count",
    tableColumns,
    tableData,
    viewData,
    activityFilter: "asset_stock_counts",
    formRoute: can(CAN_PERFORM_ASSET_STOCK_COUNT)
      ? getForm(URL_ASSET_STOCK_COUNTS)
      : undefined,
    createTitle: "New Stock Count",
    exportData: assetExport(
      API_ASSET_STOCK_COUNTS_EXPORT,
      "inventory_variance",
    ),
  };
};

export const useAssetStockCountsForm = ({
  form,
  searchParams,
  updateFormValue,
}: TFormHookParams & { searchParams?: Record<string, string> } = {}) => {
  const { search: assetSearch, asset } = useAssetPicker(form?.asset);

  useEffect(() => {
    if (searchParams?.asset) updateFormValue?.("asset", searchParams.asset);
  }, []);

  // preview only; the server computes the recorded variance
  const hasCount =
    asset &&
    form?.physical_quantity !== undefined &&
    form?.physical_quantity !== null;
  const variance = hasCount
    ? Number(form?.physical_quantity) - Number(asset?.quantity)
    : null;

  const sections: IFormSection[] = [
    {
      title: "Stock Count",
      fields: [
        {
          field_name: "asset",
          label: "Asset",
          type: "select",
          search: assetSearch,
          props: { required: true, placeholder: "Search asset by name" },
        },
        {
          field_name: "asset_summary",
          type: "component",
          visible: !!asset,
          component: <AssetSummary asset={asset} />,
        },
        {
          field_name: "physical_quantity",
          label: "Physical Quantity",
          type: "number",
          props: { required: true, showCommas: false },
        },
        {
          field_name: "variance_preview",
          type: "component",
          visible: variance !== null,
          component: (
            <p className="font12 mb-0">
              <strong>Variance:</strong> <Variance value={variance as number} />
            </p>
          ),
        },
        {
          field_name: "reason_for_variance",
          label: "Reason for Variance",
          type: "textarea",
          props: {
            required: !!variance,
            placeholder: variance
              ? "Explain the variance"
              : "Optional when there is no variance",
          },
        },
      ],
    },
  ];

  const onSubmit = (payload: any) => ({
    api: API_ASSET_STOCK_COUNTS,
    params: {
      asset: payload?.asset,
      physical_quantity: payload?.physical_quantity,
      reason_for_variance: payload?.reason_for_variance || "",
    },
  });

  return {
    sections,
    title: "Asset Stock Count",
    redirect: getRegister(URL_ASSET_STOCK_COUNTS),
    onSubmit,
  };
};

export const useAssetStockCountActionForm = createAssetApprovalActionForm(
  API_ASSET_STOCK_COUNTS,
  CAN_APPROVE_ASSET_STOCK_COUNT,
);
